import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { Icon } from "semantic-ui-react";
import { includes, filter, map, isEmpty, toLower } from "lodash";
import styles from "./styles.module.css";

const MultiSelect = ({ options, value, onChange, placeholder, disabled, error, name }) => {
	const [isOpen, setIsOpen] = useState(false);
	const [searchTerm, setSearchTerm] = useState("");
	const dropdownRef = useRef(null);

	// Close dropdown when clicking outside
	useEffect(() => {
		const handleClickOutside = (event) => {
			if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
				setIsOpen(false);
				setSearchTerm("");
			}
		};
		document.addEventListener("mousedown", handleClickOutside);
		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, []);

	const toggleDropdown = () => {
		if (!disabled) {
			setIsOpen(!isOpen);
			if (isOpen) setSearchTerm("");
		}
	};

	const handleSelect = (optionValue) => {
		let newValue = [...value];
		if (includes(newValue, optionValue)) {
			newValue = filter(newValue, (v) => {
				return v !== optionValue;
			});
		} else {
			newValue.push(optionValue);
		}
		// Mimic Semantic UI event object structure for compatibility
		onChange({ target: { name, value: newValue } }, { name, value: newValue });
	};

	const renderSelectedText = () => {
		if (isEmpty(value)) return <span className={styles.placeholder}>{placeholder}</span>;

		const selectedOptions = filter(options, (opt) => {
			return includes(value, opt.value);
		});
		return (
			<div className={styles.tagsContainer}>
				{map(selectedOptions, (opt) => {
					return (
						<span key={opt.value} className={styles.selectedTag}>
							{opt.text}
							{!disabled && (
								<Icon
									name="close"
									className={styles.removeIcon}
									onClick={(e) => {
										e.stopPropagation();
										if (!disabled) handleSelect(opt.value);
									}}
								/>
							)}
						</span>
					);
				})}
			</div>
		);
	};

	const filteredOptions = filter(options, (opt) => {
		return toLower(opt.text).includes(toLower(searchTerm));
	});

	return (
		<div
			className={`${styles.customDropdown} ${disabled ? styles.disabled : ""} ${error ? styles.error : ""} ${isOpen ? styles.open : ""}`}
			ref={dropdownRef}>
			<div
				className={styles.dropdownHeader}
				onClick={toggleDropdown}
				role="button"
				tabIndex={disabled ? -1 : 0}
				onKeyDown={(e) => {
					if (e.key === "Enter" || e.key === " ") {
						e.preventDefault();
						toggleDropdown();
					}
				}}>
				<div className={styles.headerContent}>{renderSelectedText()}</div>
				<div className={styles.headerIcon}>
					{!disabled && !isEmpty(value) && (
						<Icon
							name="close"
							className={styles.clearAllIcon}
							onClick={(e) => {
								e.stopPropagation();
								onChange({ target: { name, value: [] } }, { name, value: [] });
							}}
						/>
					)}
					<Icon name={isOpen ? "chevron up" : "chevron down"} color="grey" style={{ margin: 0 }} />
				</div>
			</div>

			{isOpen && (
				<div className={styles.dropdownListContainer}>
					<div className={styles.dropdownSearch}>
						<input
							type="text"
							className={styles.searchInput}
							placeholder="Buscar..."
							value={searchTerm}
							onChange={(e) => {
								return setSearchTerm(e.target.value);
							}}
							onClick={(e) => {
								return e.stopPropagation();
							}}
						/>
					</div>

					<ul className={styles.dropdownList}>
						{isEmpty(filteredOptions) ? (
							<li className={styles.noOptions}>No se encontraron opciones</li>
						) : (
							map(filteredOptions, (option) => {
								const isSelected = includes(value, option.value);
								return (
									// eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
									<li
										key={option.value}
										className={`${styles.dropdownItem} ${isSelected ? styles.selected : ""}`}
										onClick={(e) => {
											e.stopPropagation();
											handleSelect(option.value);
										}}>
										<div className={styles.itemText}>{option.text}</div>
										{isSelected && <Icon name="check" className={styles.checkIcon} />}
									</li>
								);
							})
						)}
					</ul>
				</div>
			)}
		</div>
	);
};

MultiSelect.propTypes = {
	options: PropTypes.arrayOf(
		PropTypes.shape({
			key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
			value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
			text: PropTypes.string,
		})
	).isRequired,
	value: PropTypes.array.isRequired,
	onChange: PropTypes.func.isRequired,
	placeholder: PropTypes.string,
	disabled: PropTypes.bool,
	error: PropTypes.bool,
	name: PropTypes.string.isRequired,
};

MultiSelect.defaultProps = {
	placeholder: "Seleccionar...",
	disabled: false,
	error: false,
};

export default MultiSelect;
